import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../elements/button'
import Text from '../../../elements/Typo/Text'
import Loader from '../../../components/loader'
import ModalCloseIconButton from '../../../components/modalCloseIconButton'
import { useCsvAnalyzer } from '../hooks/useCsvAnalyzer'
import { CsvColumnConfig, CsvColumnProfile, CsvProfile } from '../types/csv.types'
import CsvSummary from './CsvSummary'
import CsvColumnConfiguration from './CsvColumnConfiguration'
import CsvPreview from './CsvPreview'
import { BsInfoCircle } from 'react-icons/bs'
import toast from 'react-hot-toast'
import CsvColumnRenameDialog from './CsvColumnRenameDialog'
import CsvColumnNameBlockDialog from './CsvColumnNameBlockDialog'
import { ColumnRename, getColumnRenames } from '../utils/columnNameStandardizer'
import { CSV_COLUMN_NAME_MODE } from '../config'

interface Props {
    file: File | null
    onCancel: () => void
    onConfirm: (result: { blob: Blob; fileName: string; totalRows: number; delimiter: string; configs: CsvColumnConfig[] }) => void
    outputFormat?: 'csv' | 'json'
}

function toInitialConfig(column: CsvColumnProfile): CsvColumnConfig {
    return {
        name: column.name,
        detectedType: column.detectedType,
        selectedType: column.detectedType,
        typeSource: 'auto',
        decimalSeparator: column.numberFormat?.decimalSeparator,
        thousandsSeparator: column.numberFormat?.thousandsSeparator,
        dateFormat: column.detectedDateFormat,
    }
}

function buildInitialConfigs(profile: CsvProfile): CsvColumnConfig[] {
    return profile.columns.map(toInitialConfig)
}

const CsvImportDialog: React.FC<Props> = ({ file, onCancel, onConfirm, outputFormat = 'csv' }) => {
    const { t } = useTranslation()
    const dialogRef = useRef<HTMLDialogElement>(null)
    const { state, progress, profile, validations, error, analyze, validate, process, reset } = useCsvAnalyzer()
    const [configs, setConfigs] = useState<CsvColumnConfig[]>([])
    const [pendingRenames, setPendingRenames] = useState<ColumnRename[] | null>(null)

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return
        if (file) {
            if (!dialog.open) dialog.showModal()
        } else if (dialog.open) {
            dialog.close()
        }
    }, [file])

    useEffect(() => {
        if (!file) return
        reset()
        analyze(file)
            .then((analyzedProfile) => {
                console.log('[CSV Import] detected file encoding:', analyzedProfile.encoding, { name: file.name, size: file.size, type: file.type })
                if (analyzedProfile.duplicateColumnNames.length > 0) {
                    toast.error(t('Duplicate column names found: {{columns}}. Please fix the file and select it again.', { columns: analyzedProfile.duplicateColumnNames.join(', ') }))
                    handleClose()
                    return
                }
                const initial = buildInitialConfigs(analyzedProfile)
                setConfigs(initial)
                return validate(initial, analyzedProfile.previewRows)
            })
            .catch(() => undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file])

    const previewRows = useMemo(() => profile?.previewRows ?? [], [profile])

    const handleConfigChange = (next: CsvColumnConfig[]) => {
        setConfigs(next)
        if (profile) validate(next, profile.previewRows).catch(() => undefined)
    }

    const runProcess = async (finalConfigs: CsvColumnConfig[]) => {
        if (!file || !profile) return
        try {
            const result = await process(file, profile.delimiter, finalConfigs, outputFormat, profile.encoding)
            onConfirm({ blob: result.blob, fileName: result.fileName, totalRows: result.totalRows, delimiter: profile.delimiter, configs: finalConfigs })
        } catch {
            // Error state handled by hook.
        }
    }

    const handleConfirm = async () => {
        if (!file || !profile) return
        const renames = getColumnRenames(configs.map((config) => config.name))
        if (renames.length > 0) {
            setPendingRenames(renames)
            return
        }
        await runProcess(configs)
    }

    const handleAcceptRenames = () => {
        const renameMap = new Map((pendingRenames ?? []).map((rename) => [rename.original, rename.standardized]))
        setPendingRenames(null)
        void runProcess(configs.map((config) => ({ ...config, outputName: renameMap.get(config.name) })))
    }

    const handleClose = () => {
        setPendingRenames(null)
        reset()
        onCancel()
    }

    const isBusy = state === 'analyzing' || state === 'processing'

    const themeHeading = (heading: string) => {
        return (
            <div className="relative">
                <div className="absolute -left-4 z-10 top-1/2 -translate-y-[50%] h-full w-[0.35rem] bg-primary rounded-e-md"></div>
                <Text size="md" className="font-semibold !leading-6">
                    {heading}
                </Text>
            </div>
        )
    }

    return (
        <>
            <dialog data-testid="csv-import-dialog" ref={dialogRef} className="modal">
                <div className="modal-box font-sans !p-4 !max-w-[64rem] !w-[64rem]">
                    <ModalCloseIconButton onClick={handleClose} />
                    <div className="flex flex-col gap-3 text-[0.6rem] leading-[1.35]">
                        <div className="bg-[#325A6D1A] -mx-4 px-4 -mt-4 pt-4 rounded-t-md overflow-hidden">
                            <div className="bg-[#ecd96e] mb-1 -mx-4 px-4 -mt-4 py-2 border-b border-[#ffffff]">
                                <Text size="lg" className="font-semibold">
                                    {t('CSV Import')}
                                </Text>
                            </div>

                            {profile && (state === 'configuring' || state === 'validating' || state === 'ready' || state === 'processing' || state === 'complete') && <CsvSummary profile={profile} />}
                        </div>

                        {state === 'analyzing' && (
                            <div className="flex flex-col items-center gap-2 py-6">
                                <Loader />
                                <Text size="md" className="italic text-slate-600">
                                    {t('Analyzing CSV...')} {progress ? `${progress.progress}%` : ''}
                                </Text>
                            </div>
                        )}

                        {state === 'error' && (
                            <div className="bg-red-50 border border-red-200 rounded p-2">
                                <Text size="md" className="!text-red-700 font-semibold">
                                    {t('Analysis failed')}: {error}
                                </Text>
                            </div>
                        )}

                        {profile && (state === 'configuring' || state === 'validating' || state === 'ready' || state === 'processing' || state === 'complete') && (
                            <>
                                <div className="border border-[#efd98a] rounded-md p-2 -mx-4 bg-gradient-to-r angle-[135deg] from-[#FFFBEF] to-[#FEF5D8] flex items-center gap-2">
                                    <BsInfoCircle className="text-[1.25rem] text-[#7c640b] p-1 bg-primary rounded-full flex-shrink-0" />
                                    <p className="flex-grow">
                                        We have scanned your file and <span className="font-medium">auto-detected a data type for each column</span>. Review the results below — if a type looks wrong, change it using the dropdown in the{' '}
                                        <span className="font-medium">Type</span> column. Everything else here is read-only. Once you are happy with the setup, confirm to start the import.
                                    </p>
                                </div>

                                {themeHeading(t('Column Configuration'))}

                                <CsvColumnConfiguration profile={profile} configs={configs} validations={validations} onChange={handleConfigChange} />

                                {themeHeading(t('Data Preview'))}

                                <CsvPreview columns={profile.columns} rows={previewRows} />
                            </>
                        )}

                        {state === 'processing' && progress && (
                            <div className="mt-2">
                                <div className="w-full h-1 bg-slate-200 rounded overflow-hidden">
                                    <div className="h-full bg-primary" style={{ width: `${progress.progress}%` }} />
                                </div>
                                <Text size="sm" className="!text-slate-500 mt-1">
                                    {t('Processing')}... {progress.progress}%
                                </Text>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mt-2">
                            <Button theme="secondary" styleType="outline" size="xs" onClick={handleClose} disabled={isBusy}>
                                {t('Cancel')}
                            </Button>
                            <Button theme="primary" size="xs" onClick={handleConfirm} disabled={!profile || isBusy}>
                                {t('Continue')}
                            </Button>
                        </div>
                    </div>
                </div>
            </dialog>
            {CSV_COLUMN_NAME_MODE === 'block' ? <CsvColumnNameBlockDialog renames={pendingRenames} onReupload={handleClose} /> : <CsvColumnRenameDialog renames={pendingRenames} onAccept={handleAcceptRenames} onReupload={handleClose} />}
        </>
    )
}

export default CsvImportDialog
